// Tested by __tests__/useLiveTrace.test.tsx — the terminal-detection /
// persisted-seed / SSE-lifecycle logic here regressed twice from subtle
// effect-dependency changes, so it now has a dedicated hook test
// (renderHook + a fake EventSource) guarding: completed-run-opened-fresh
// seeds from the persisted trace (no "loading…" hang) and closes its
// stream (no reconnect loop), while in-flight runs keep the stream open.
// Reducer state-shape correctness remains covered by
// traceMirrorReducer.test.ts; wire-format → action shape by the events
// package's TestBus_AnchorTypes_* + the orchestrator-side diag_patch_test.go.
import { useEffect, useMemo, useReducer, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  fetchRunTrace,
  type RunTrace,
  type SubCallTrace,
} from "../lib/traceApi";
import {
  initMirrorState,
  traceMirrorReducer,
  type TracePatchOp,
} from "../state/traceMirrorReducer";

// useLiveTrace consolidates the post-hoc trace query and the live SSE
// stream into a single source of truth backed by the 0062 mirror
// architecture: the server emits trace_snapshot (periodic anchors) +
// trace_patch (RFC 6902 mutations); the mirror reducer applies them in
// order to produce a RunTrace shape identical to the persisted doc.
//
// On run completion, the persisted-trace query result re-seeds the
// mirror reducer (covers already-completed page loads where SSE has no
// events to deliver).
//
// Returned shape:
//   trace      — RunTrace | null. Null only on initial mount before
//                any snapshot anchor or persisted query arrives.
//   source     — "live" while SSE-driven; "persisted" after run-end
//                swap; "empty" pre-mount.
//   liveStatus — EventSource connection state.
//   runStatus  — RunRecord.status from the persisted query payload.
//   runRecord  — full run record (TraceBody uses for summary stats).
//   patchErrors — mirror reducer's patch-apply errors (drift signal;
//                 empty in the happy path).

export type LiveTraceStatus = "connecting" | "open" | "closed" | "error";
export type TraceSource = "live" | "persisted" | "empty";

export interface UseLiveTraceResult {
  trace: RunTrace | null;
  source: TraceSource;
  liveStatus: LiveTraceStatus;
  runStatus: string;
  runRecord: unknown;
  patchErrors: string[];
}

interface SSEEnvelope {
  [key: string]: unknown;
}

// Closed-set of admin event types the hook subscribes to.
// trace_snapshot + trace_patch drive state. run_complete + run_error +
// run_paused (0064: a saved pass's segment ended; it waits for the
// admin) + trace_persistence_failed control the live-stream close +
// persisted-swap flow. T16 (backend drift test) enforces parity against
// newEvent literals in event_types.go.
const KNOWN_ADMIN_EVENTS = [
  "trace_snapshot",
  "trace_patch",
  "run_complete",
  "run_error",
  "run_paused",
  "trace_persistence_failed",
  // Founder-track events that flow on the admin stream as a side
  // effect of single-publish-with-filter; the hook ignores them but
  // they're listed so T16 sees a handler in this file for closed-set
  // parity.
  "stage_start",
  "stage_complete",
  "stage_aborted",
  "retry",
  "node_created",
  "ring_complete",
  "user_progress",
  "credit_decremented",
  "credit_refunded",
  "draft_applied",
  "drafts_processed",
  "run_finalized",
  "run_started",
  "admin_stream_unavailable",
  "admin_stream_post_hoc",
];

// stripNullSubCalls returns a render-safe COPY of the trace with null sub-call
// slots removed. The live-trace mirror's canonical state can hold null
// sub_calls entries mid-run — in-flight slots the server emits before the
// sub-call's data lands — and the trace components deref sc.sub_prompt_id /
// sc.item_key, crashing the whole page (black screen) on a live run. We strip
// them ONLY for rendering: the reducer's canonical trace is left untouched so
// RFC-6902 patch paths (/stages/i/layers/j/sub_calls/k) stay index-aligned.
// Anchors key on sub_prompt_id+item_key (not array index), so removing nulls
// is safe for the UI. Stages/layers are defensively filtered too.
export function stripNullSubCalls(trace: RunTrace | null): RunTrace | null {
  if (!trace) return null;
  return {
    ...trace,
    stages: ((trace.stages ?? []) as (RunTrace["stages"][number] | null)[])
      .filter((s): s is RunTrace["stages"][number] => s != null)
      .map((stage) => ({
        ...stage,
        layers: ((stage.layers ?? []) as (typeof stage.layers[number] | null)[])
          .filter((l): l is typeof stage.layers[number] => l != null)
          .map((layer) => ({
            ...layer,
            sub_calls: ((layer.sub_calls ?? []) as (SubCallTrace | null)[]).filter(
              (sc): sc is SubCallTrace => sc != null,
            ),
          })),
      })),
  };
}

export function useLiveTrace(
  uid: string,
  pid: string,
  rid: string,
): UseLiveTraceResult {
  const [mirrorState, mirrorDispatch] = useReducer(
    traceMirrorReducer,
    rid ? initMirrorState(rid) : initMirrorState(""),
  );
  const [liveStatus, setLiveStatus] = useState<LiveTraceStatus>("connecting");
  const [runEnded, setRunEnded] = useState(false);

  // Re-init reducer when the rid changes (navigating between runs).
  useEffect(() => {
    if (rid) {
      mirrorDispatch({ type: "RESET", payload: { runId: rid } });
      setRunEnded(false);
    }
  }, [rid]);

  // Persisted trace query. Initial enabled is always (rid is set);
  // refetches on poll while running, and we explicitly refetch on
  // run_complete / run_error to pick up the just-written trace.
  const traceQ = useQuery({
    queryKey: ["admin", "run-trace", uid, pid, rid],
    queryFn: () => fetchRunTrace(uid, pid, rid),
    enabled: Boolean(uid && pid && rid),
    refetchInterval: (q) => {
      const d = q.state.data as { trace: RunTrace | null; run: { status?: string } } | undefined;
      if (!d) return false;
      const status = d.run?.status ?? "";
      if (status === "running") return 5000;
      return false;
    },
  });

  const queryTrace = (traceQ.data?.trace as RunTrace | undefined) ?? null;
  const runStatus =
    ((traceQ.data?.run as { status?: string } | undefined)?.status ?? "");

  // Terminal detection — DECOUPLED from the SSE subscription effect.
  //
  // traceQ is async: at mount runStatus is "" (no fetch resolved yet),
  // resolving to "running" or a terminal value ("success" / "failed_*"
  // / etc.) a beat later. This effect watches runStatus and flips
  // runEnded the moment it resolves terminal — regardless of WHEN that
  // happens relative to mount. runEnded then (a) gates the persisted-
  // seed below and (b) closes the SSE effect.
  //
  // Why a separate effect (not folded into the SSE effect): a prior
  // version did the terminal check INSIDE the SSE effect with deps
  // [uid,pid,rid]. For an already-completed run opened fresh, the check
  // ran once at mount while runStatus was still "" — so it never fired,
  // runEnded never flipped, the persisted-seed never ran (permanent
  // "loading…"), and the SSE opened to a done run and auto-reconnected
  // forever. Decoupling makes terminal detection react to the async
  // runStatus resolution without coupling it to subscription churn.
  useEffect(() => {
    if (runStatus !== "" && runStatus !== "running") {
      setRunEnded(true);
      setLiveStatus("closed");
    }
  }, [runStatus]);

  // Seed mirror state from the persisted trace once the run has ended
  // (or page-loaded against an already-completed run). Without this,
  // mirror.trace would stay null when no SSE events arrive.
  useEffect(() => {
    if (!runEnded || !queryTrace) return;
    mirrorDispatch({
      type: "TRACE_SNAPSHOT",
      payload: { trace: queryTrace },
    });
  }, [runEnded, queryTrace]);

  // SSE subscription. Opens an EventSource only while the run is live;
  // closes when runEnded flips true. runEnded IS in the deps on purpose:
  // it is what STOPS the EventSource auto-reconnect loop for an
  // already-completed run. Flow for a completed run opened fresh: at
  // mount runEnded=false (status unknown) so we open briefly; the
  // terminal-detection effect above flips runEnded once traceQ resolves;
  // this effect re-runs, the cleanup closes the stream, and the
  // `if (runEnded) return` guard prevents reopening. For an in-flight
  // run, runEnded stays false (status "running") so the stream stays
  // open until the run_complete / run_error handler flips it.
  //
  // runStatus is intentionally NOT in deps — runEnded (a single
  // false→true flip) carries the terminal signal, so the stream
  // subscribes once and tears down once, with no per-poll churn.
  useEffect(() => {
    if (!uid || !pid || !rid) return;
    if (runEnded) return; // run already terminal — no live stream
    const apiBase: string =
      (import.meta.env.VITE_API_BASE_URL as string | undefined) ??
      (typeof window !== "undefined" ? window.location.origin : "");
    const url = `${apiBase}/api/v1/admin/users/${uid}/products/${pid}/runs/${rid}/stream`;
    const evt = new EventSource(url, { withCredentials: true });
    setLiveStatus("connecting");

    const handler = (e: MessageEvent) => {
      let data: SSEEnvelope = {};
      try {
        data = JSON.parse(e.data);
      } catch {
        data = { raw: e.data };
      }
      dispatchSSEEvent(mirrorDispatch, e.type, data);
      // run_paused ends the live stream like run_complete / run_error: the
      // pass waits for the admin (a resume re-mounts the trace).
      if (e.type === "run_complete" || e.type === "run_error" || e.type === "run_paused") {
        evt.close();
        setLiveStatus("closed");
        setRunEnded(true);
        traceQ.refetch();
      }
    };

    for (const t of KNOWN_ADMIN_EVENTS) {
      evt.addEventListener(t, handler as EventListener);
    }
    evt.onopen = () => setLiveStatus("open");
    evt.onerror = () => setLiveStatus("error");

    return () => {
      evt.close();
    };
    // traceQ.refetch is referentially stable per TanStack docs; excluded
    // from deps to avoid resubscribing every poll-cycle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid, pid, rid, runEnded]);

  const source: TraceSource = !rid
    ? "empty"
    : runEnded
      ? "persisted"
      : mirrorState.trace !== null
        ? "live"
        : "empty";

  // Sanitize for render (strip null sub-call slots) without mutating the
  // canonical patch-aligned reducer state. Memoized so consumers don't see a
  // new object reference every render.
  const renderTrace = useMemo(
    () => stripNullSubCalls(mirrorState.trace),
    [mirrorState.trace],
  );

  return {
    trace: renderTrace,
    source,
    liveStatus,
    runStatus,
    runRecord: traceQ.data?.run ?? null,
    patchErrors: mirrorState.patchErrors,
  };
}

// dispatchSSEEvent forwards trace_snapshot / trace_patch into the
// mirror reducer. Other admin-stream events (founder-track passthroughs,
// run lifecycle signals) are no-ops here — the caller-side handler
// inspects e.type for run_complete / run_error close detection.
function dispatchSSEEvent(
  mirrorDispatch: React.Dispatch<
    | { type: "TRACE_SNAPSHOT"; payload: { trace: RunTrace } }
    | { type: "TRACE_PATCH"; payload: { ops: TracePatchOp[] } }
    | { type: "RESET"; payload: { runId: string } }
  >,
  type: string,
  data: SSEEnvelope,
): void {
  switch (type) {
    case "trace_snapshot": {
      const trace = data.trace;
      if (trace && typeof trace === "object") {
        mirrorDispatch({
          type: "TRACE_SNAPSHOT",
          payload: { trace: trace as RunTrace },
        });
      }
      return;
    }
    case "trace_patch": {
      const ops = data.ops;
      if (Array.isArray(ops)) {
        mirrorDispatch({
          type: "TRACE_PATCH",
          payload: { ops: ops as TracePatchOp[] },
        });
      }
      return;
    }
    default:
      return;
  }
}
